import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AGENT_PAGES } from "@/content/ai-team/agent-pages";
import { aiCrew as agents } from "@/lib/agents";

interface AiTeamMenuProps {
  open: boolean;
  onClose: () => void;
}

const AiTeamMenu = ({ open, onClose }: AiTeamMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      className="fixed left-1/2 top-10 z-50 w-[540px] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-white/10 bg-card/40 backdrop-blur-2xl p-5 shadow-2xl"
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {agents.map((agent) => {
          const href = AGENT_PAGES[agent.name];
          const inner = (
            <>
              <img
                src={agent.image}
                alt={agent.name}
                loading="lazy"
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />
              <div className="flex flex-col min-w-0 flex-1 gap-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground truncate">{agent.name}</span>
                  {!href && (
                    <span className="shrink-0 rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Coming soon
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground truncate">{agent.role}</span>
              </div>
            </>
          );
          return href ? (
            <Link
              key={agent.name}
              to={href}
              onClick={onClose}
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/40"
            >
              {inner}
            </Link>
          ) : (
            <div
              key={agent.name}
              aria-disabled="true"
              className="group flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-not-allowed"
            >
              {inner}
            </div>
          );
        })}
      </div>
      <Link
        to="/ai-team"
        onClick={onClose}
        className="mt-3 flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-card/40 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary"
      >
        Meet the full crew
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

export default AiTeamMenu;
